import React, { useState, useEffect, useRef } from 'react';
import { addMessage, onMessagesChange, uploadMessageAttachment } from '../../firebase/messages';
import { markNotificationsAsRead } from '../../firebase/notifications';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { X, Paperclip, Send } from 'lucide-react';

export const MessagingPanel = ({ projectId, element, currentUser, participants, allUsers, onClose, context }) => {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [attachment, setAttachment] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const messagesEndRef = useRef(null);
    const [userProfiles, setUserProfiles] = useState({});

    useEffect(() => {
        if (!projectId || !element?.id) return;

        markNotificationsAsRead(currentUser.uid, element.id);
        const unsubscribe = onMessagesChange(projectId, element.id, setMessages);
        return () => unsubscribe();
    }, [projectId, element?.id, currentUser.uid]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    useEffect(() => {
        const fetchAndSetProfiles = async () => {
            const profilesMap = new Map();
            let profilesChanged = false;

            // Start with current state
            Object.entries(userProfiles).forEach(([id, profile]) => profilesMap.set(id, profile));

            // Add profiles from allUsers prop if they aren't already there.
            allUsers.forEach(user => {
                if (user && user.id && !profilesMap.has(user.id)) {
                    profilesMap.set(user.id, user);
                    profilesChanged = true;
                }
            });

            // Find user IDs from messages that are not yet in our map.
            const allMessageUserIds = [...new Set(messages.map(m => m.userId))];
            const missingIds = allMessageUserIds.filter(id => id && !profilesMap.has(id));

            if (missingIds.length > 0) {
                const userPromises = missingIds.map(uid => getDoc(doc(db, 'users', uid)));
                try {
                    const userDocs = await Promise.all(userPromises);
                    userDocs.forEach(docSnap => {
                        if (docSnap.exists()) {
                            profilesMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
                            profilesChanged = true;
                        }
                    });
                } catch (error) {
                    console.error("Error fetching missing user profiles:", error);
                }
            }

            if (profilesChanged) {
                setUserProfiles(Object.fromEntries(profilesMap));
            }
        };
        fetchAndSetProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, allUsers]);

    const handleSendMessage = async () => {
        if (!newMessage.trim() && !attachment) return;

        setIsSubmitting(true);
        let imageUrl = null;

        try {
            if (attachment) {
                imageUrl = await uploadMessageAttachment(projectId, attachment, () => {});
            }

            const messageData = {
                text: newMessage,
                userId: currentUser.uid,
                imageUrl,
            };

            await addMessage(projectId, element.id, messageData, participants);
            
            setNewMessage('');
            setAttachment(null);
        } catch (error) {
            console.error("Failed to send message:", error);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setAttachment(e.target.files[0]);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            setAttachment(e.dataTransfer.files[0]);
        }
    };
    
    const getUserById = (id) => userProfiles[id];

    return (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-[70]" onClick={onClose}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <header className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
                    <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200">Feedback on "{element?.name}" <span className="text-gray-400 font-normal">({context})</span></h3>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"><X className="h-5 w-5"/></button>
                </header>
                <div className="flex-grow p-4 overflow-y-auto" ref={messagesEndRef}>
                    <div className="space-y-4">
                        {messages.map(msg => {
                            const sender = getUserById(msg.userId);
                            const isCurrentUser = msg.userId === currentUser.uid;
                            return (
                                <div key={msg.id} className={`flex items-start gap-3 ${isCurrentUser ? 'justify-end' : ''}`}>
                                    {!isCurrentUser && (
                                        <div className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center font-bold text-sm text-white flex-shrink-0">
                                            {sender?.displayName?.[0] || '?'}
                                        </div>
                                    )}
                                    <div className={`p-3 rounded-lg max-w-xs lg:max-w-md ${isCurrentUser ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700'}`}>
                                        <p className="text-sm font-bold mb-1">{sender?.displayName || 'Unknown User'}</p>
                                        {msg.text && <p className="text-sm">{msg.text}</p>}
                                        {msg.imageUrl && (
                                            <img src={msg.imageUrl} alt="attachment" className="mt-2 rounded-lg max-h-48 cursor-pointer" onClick={() => window.open(msg.imageUrl, '_blank')} />
                                        )}
                                        <p className="text-xs opacity-70 mt-2 text-right">{msg.createdAt?.toDate().toLocaleTimeString()}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <footer 
                    className={`p-4 border-t dark:border-gray-700 ${isDragging ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
                    onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
                    onDrop={handleDrop}
                >
                    {attachment && (
                        <div className="relative mb-2 p-2 border dark:border-gray-600 rounded-lg flex items-center justify-between">
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">Attached: {attachment.name}</p>
                            <button onClick={() => setAttachment(null)} className="p-1 text-gray-400 hover:text-red-500"><X className="h-4 w-4"/></button>
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && !isSubmitting && handleSendMessage()}
                            placeholder="Type a message..."
                            className="flex-grow bg-gray-100 dark:bg-gray-700 border-transparent focus:border-blue-500 focus:ring-blue-500 rounded-lg px-4 py-2"
                        />
                        <input type="file" id="file-upload" className="hidden" onChange={handleFileChange} />
                        <label htmlFor="file-upload" className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
                            <Paperclip className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                        </label>
                        <button onClick={handleSendMessage} disabled={isSubmitting} className="p-2 bg-blue-600 text-white rounded-full disabled:bg-blue-300">
                            <Send className="h-5 w-5" />
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
};
